import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import QuickConverter from '../components/QuickConverter';
import CSSRotatingCircle from '../components/CSSRotatingCircle';
import ScrollAnimations from '../components/ScrollAnimations';
import RotatingBackgroundCircle from '../components/RotatingBackgroundCircle';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  color: #000000;
  overflow-x: hidden;
  overflow-y: ${props => props.allowVerticalScroll ? 'auto' : 'hidden'};
`;

// Main horizontal scrolling container
const HorizontalContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  padding-top: 80px; /* Account for fixed navigation */
  background-color: ${props => props.isInHorizontalSection ? '#000000' : '#ffffff'};
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  overflow: visible; /* Allow overflow for ring visibility */
  transition: background-color 0.8s ease, color 0.8s ease;
  
  @media (max-width: 768px) {
    padding-top: 70px; /* Smaller padding on mobile */
  }
`;

// Horizontal scroll wrapper
const HorizontalScrollWrapper = styled.div`
  display: flex;
  width: 500vw; /* 5 sections */
  height: 100vh;
  will-change: transform;
  transition: transform 0.1s ease-out;
`;

// Individual section
const Section = styled.div`
  width: 100vw;
  height: 100vh; /* Full height */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  padding: 0 5vw;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    height: 100vh; /* Full height on mobile too */
  }
`;

// Section content wrapper
const SectionContent = styled.div`
  max-width: 1200px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

// Left content area
const LeftContent = styled.div`
  flex: 1;
  max-width: 50%;
  padding-right: 5vw;
`;

// Right content area
const RightContent = styled.div`
  flex: 1;
  max-width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

// Typography components
const SectionTitle = styled.h1`
  font-size: clamp(48px, 8vw, 96px);
  line-height: clamp(44px, 7vw, 88px);
  letter-spacing: -2px;
  font-weight: 700;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  margin-bottom: 2rem;
  transition: color 0.8s ease;
  opacity: ${props => props.opacity || 1};
  transform: ${props => props.transform || 'none'};
  transition: all 0.3s ease;
`;

// Large header text for last section
const LargeHeaderText = styled.h1`
  font-size: clamp(72px, 12vw, 144px);
  line-height: clamp(68px, 11vw, 136px);
  letter-spacing: -3px;
  font-weight: 700;
  margin: 0;
  color: ${props => props.isVerticalSection ? '#000000' : '#ffffff'};
  text-align: center;
  position: relative;
  z-index: 2;
  opacity: ${props => props.opacity || 1};
  transform: ${props => props.transform || 'none'};
  transition: all 0.5s ease;
`;

const SectionSubtitle = styled.h2`
  font-size: clamp(28px, 4.5vw, 48px);
  line-height: clamp(28px, 4.5vw, 48px);
  letter-spacing: -2px;
  font-weight: 600;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  margin-bottom: 1.5rem;
  transition: color 0.8s ease;
`;

const SectionDescription = styled.p`
  font-size: clamp(16px, 2.5vw, 20px);
  line-height: clamp(20px, 3vw, 24px);
  letter-spacing: -0.5px;
  font-weight: 400;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  margin-bottom: 2rem;
  transition: color 0.8s ease;
`;

const BrandDescription = styled.p`
  font-size: clamp(14px, 2vw, 16px);
  line-height: clamp(18px, 2.5vw, 20px);
  letter-spacing: -0.5px;
  font-weight: 400;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  margin-bottom: 2rem;
  transition: color 0.8s ease;
`;

const ContactLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  li {
    a {
      color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
      text-decoration: none;
      font-size: clamp(14px, 2vw, 16px);
      position: relative;
      transition: color 0.8s ease;
      
      &:hover {
        color: #767676;
      }
      
      .underline {
        background-color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
        bottom: -2px;
        height: 1px;
        left: 0;
        position: absolute;
        width: 0;
        transition: width 0.2s cubic-bezier(0.455, 0.03, 0.515, 0.955), background-color 0.8s ease;
      }
      
      &:hover .underline {
        width: 100%;
      }
    }
  }
`;

const TimeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  font-size: clamp(14px, 2vw, 16px);
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  transition: color 0.8s ease;
`;

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;



const ServiceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    font-size: clamp(16px, 2.5vw, 20px);
    line-height: clamp(20px, 3vw, 24px);
    color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
    margin-bottom: 1rem;
    position: relative;
    padding-left: 1.5rem;
    transition: color 0.8s ease;
    
    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
      transition: color 0.8s ease;
    }
  }
`;

const ProcessBlock = styled.div`
  margin-bottom: 2rem;
`;

const ProcessTitle = styled.h3`
  font-size: clamp(20px, 3vw, 24px);
  line-height: clamp(24px, 3.5vw, 30px);
  letter-spacing: -1px;
  font-weight: 600;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  margin-bottom: 1rem;
  transition: color 0.8s ease;
`;

const ProcessDescription = styled.p`
  font-size: clamp(14px, 2vw, 16px);
  line-height: clamp(18px, 2.5vw, 20px);
  letter-spacing: -0.5px;
  font-weight: 400;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  transition: color 0.8s ease;
`;

// Large numeral component
const LargeNumeral = styled.div`
  font-size: clamp(200px, 40vw, 500px);
  font-weight: 700;
  color: #ffffff;
  line-height: 0.8;
  letter-spacing: -0.02em;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  user-select: none;
  pointer-events: none;
`;

// White square with black circle
const WhiteSquareWithCircle = styled.div`
  width: clamp(200px, 25vw, 400px);
  height: clamp(200px, 25vw, 400px);
  background: #ffffff;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    width: 60%;
    height: 60%;
    background: #000000;
    border-radius: 50%;
  }
`;



// Single Navigation component
const FixedNavigation = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${props => props.isInHorizontalSection ? '#000000' : '#ffffff'};
  backdrop-filter: ${props => props.isInHorizontalSection ? 'none' : 'blur(10px)'};
  z-index: 1000;
  padding: 1rem 2rem;
  border-bottom: ${props => props.isInHorizontalSection ? 'none' : '1px solid #e8e8e8'};
  transition: background 0.8s ease, backdrop-filter 0.8s ease, border-bottom 0.8s ease;
`;

// Fixed SVG component - transitions from normal to fixed position
const FixedSVG = styled.div`
  position: ${props => props.isFixed ? 'fixed' : 'absolute'};
  left: ${props => props.isFixed ? '50px' : '0'};
  right: ${props => props.isFixed ? 'auto' : '0'};
  top: 50%;
  transform: translateY(-50%) translateX(${props => props.movement || '0px'});
  z-index: ${props => props.isFixed ? '1000' : '10'};
  width: auto;
  height: auto;
  transition: all 0.3s ease;
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 400;
  color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
  transition: color 0.8s ease;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  a {
    color: ${props => props.isInHorizontalSection ? '#ffffff' : '#000000'};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.8s ease;
    
    &:hover {
      color: #767676;
    }
  }
`;

// Vertical parallax section
const VerticalSection = styled.div`
  min-height: 100vh;
  background: #ffffff;
  position: relative;
`;

const ConverterSection = styled.div`
  padding: 8rem 2rem 4rem;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const ConverterTitle = styled.h2`
  font-size: clamp(48px, 8vw, 72px);
  line-height: clamp(44px, 7vw, 68px);
  letter-spacing: -2px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const ConverterDescription = styled.p`
  font-size: clamp(16px, 2.5vw, 20px);
  line-height: clamp(20px, 3vw, 24px);
  color: #767676;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ConverterWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2.5rem;
  background: #f8f8f8;
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
`;

const TwooLandingPage = () => {
  const containerRef = useRef(null);
  const scrollWrapperRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVerticalSection, setIsVerticalSection] = useState(false);
  const [isInHorizontalSection, setIsInHorizontalSection] = useState(true);
  const [allowVerticalScroll, setAllowVerticalScroll] = useState(false);
  const [horizontalProgress, setHorizontalProgress] = useState(0);
  const [isSvgSticky, setIsSvgSticky] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [savedHorizontalProgress, setSavedHorizontalProgress] = useState(0);
  const svgRef = useRef(null);
  const thirdSectionRef = useRef(null);
  const [thirdSectionTextScale, setThirdSectionTextScale] = useState(1);
  const [thirdSectionTextOpacity, setThirdSectionTextOpacity] = useState(1);
  const [lastSectionTextOpacity, setLastSectionTextOpacity] = useState(0);
  const [lastSectionTextScale, setLastSectionTextScale] = useState(0.8);

  // SVG translates and rotates to the right on scroll
  useEffect(() => {
    if (!svgRef.current) return;

    // Calculate movement and rotation based on horizontal progress
    const movement = horizontalProgress * 500; // Move 500px to the right
    const rotation = horizontalProgress * 360; // Rotate 360 degrees
    
    // Apply translation and rotation
    svgRef.current.style.transform = `translateY(-50%) translateX(${movement}px) rotate(${rotation}deg)`;
  }, [horizontalProgress]);


  // Text effects based on scroll progress
  useEffect(() => {
    // Third section text effects - scale up and fade out as we approach last section
    const thirdSectionProgress = Math.max(0, Math.min(1, (horizontalProgress - 0.5) / 0.3));
    const thirdSectionScale = 1 + thirdSectionProgress * 0.5; // Scale up to 1.5x
    setThirdSectionTextScale(thirdSectionScale);
    setThirdSectionTextOpacity(1 - thirdSectionProgress * 0.7); // Fade to 30% opacity

    // Last section text effects - fade in and scale up when we reach it
    const lastSectionProgress = Math.max(0, Math.min(1, (horizontalProgress - 0.7) / 0.3));
    const lastSectionScale = 0.8 + lastSectionProgress * 0.2; // Scale from 0.8 to 1.0
    setLastSectionTextOpacity(lastSectionProgress);
    setLastSectionTextScale(lastSectionScale);
  }, [horizontalProgress]);

  useEffect(() => {
    const handleScroll = (e) => {
      const currentScrollY = window.pageYOffset;
      const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';
      setLastScrollY(currentScrollY);

      // If we're scrolling up and we're in vertical section, go back to horizontal
      if (scrollDirection === 'up' && isVerticalSection && currentScrollY < 50) {
        setIsVerticalSection(false);
        setIsInHorizontalSection(true);
        setAllowVerticalScroll(false);
        // Restore the saved horizontal progress instead of resetting to 0
        setHorizontalProgress(savedHorizontalProgress);
        
        // Restore the horizontal scroll position
        const translateX = -savedHorizontalProgress * (400 - 100);
        if (scrollWrapperRef.current) {
          scrollWrapperRef.current.style.transform = `translateX(${translateX}vw)`;
        }
        
        document.body.style.overflow = 'hidden';
        return;
      }

      // If we're still in horizontal mode, prevent vertical scrolling
      if (!allowVerticalScroll) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleWheel = (e) => {
      const currentScrollY = window.pageYOffset;
      const scrollDirection = e.deltaY > 0 ? 'down' : 'up';

      // If we're in vertical section and scrolling up near the top, go back to horizontal
      if (isVerticalSection && scrollDirection === 'up' && currentScrollY < 30) {
        setIsVerticalSection(false);
        setIsInHorizontalSection(true);
        setAllowVerticalScroll(false);
        // Restore the saved horizontal progress instead of resetting to 0
        setHorizontalProgress(savedHorizontalProgress);
        
        // Restore the horizontal scroll position
        const translateX = -savedHorizontalProgress * (400 - 100);
        if (scrollWrapperRef.current) {
          scrollWrapperRef.current.style.transform = `translateX(${translateX}vw)`;
        }
        
        document.body.style.overflow = 'hidden';
        return;
      }

      // If we're still in horizontal mode, convert vertical scroll to horizontal
      if (!allowVerticalScroll) {
        e.preventDefault();
        
        const delta = e.deltaY;
        const newProgress = Math.max(0, Math.min(1, horizontalProgress + (delta * 0.001)));
        setHorizontalProgress(newProgress);
        
        // Update horizontal scroll position
        const translateX = -newProgress * (400 - 100); // 400vw - 100vw (4 sections instead of 5)
        if (scrollWrapperRef.current) {
          scrollWrapperRef.current.style.transform = `translateX(${translateX}vw)`;
        }
        
        // Start transition when we're 95% through horizontal scroll
        if (newProgress > 0.95 && !isTransitioning) {
          setIsTransitioning(true);
        }
        
        // Complete horizontal scroll and enable vertical
        if (newProgress >= 1.0 && !allowVerticalScroll) {
          // Save the current progress before switching to vertical
          setSavedHorizontalProgress(newProgress);
          
          setAllowVerticalScroll(true);
          setIsVerticalSection(true);
          setIsInHorizontalSection(false);
          
          // Ensure horizontal content is fully scrolled
          if (scrollWrapperRef.current) {
            scrollWrapperRef.current.style.transform = `translateX(-300vw)`;
          }
          
          // Allow normal scrolling to resume
          document.body.style.overflow = 'auto';
        }
      }
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: false });
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    // Prevent default scrolling behavior during horizontal mode
    if (!allowVerticalScroll) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'auto';
    };
  }, [allowVerticalScroll, horizontalProgress, isTransitioning, isVerticalSection, lastScrollY, savedHorizontalProgress]);

  return (
    <PageContainer allowVerticalScroll={allowVerticalScroll}>
      {/* Single Fixed Navigation - Only One Set of Elements */}
      <FixedNavigation isInHorizontalSection={isInHorizontalSection}>
        <NavContent>
          <Logo isInHorizontalSection={isInHorizontalSection}>Warp</Logo>
          <NavLinks isInHorizontalSection={isInHorizontalSection}>
            <Link to="/">Home</Link>
            <Link to="/login">Login</Link>
            <Link to="/dashboard">Dashboard</Link>
          </NavLinks>
        </NavContent>
      </FixedNavigation>

      {/* SVG moved back to original position for proper detection */}

      {/* Horizontal parallax sections */}
      <HorizontalContainer ref={containerRef} isInHorizontalSection={isInHorizontalSection}>
        <HorizontalScrollWrapper ref={scrollWrapperRef}>
          {/* Section 1: Hero - Warp Platform */}
          <Section>
            <SectionContent>
              <LeftContent>
                <ScrollAnimations animationType="slideInLeft" delay={0.2}>
                  <SectionSubtitle isInHorizontalSection={isInHorizontalSection}>
                    The Future of Cross-Border Payments
                  </SectionSubtitle>
                  <BrandDescription isInHorizontalSection={isInHorizontalSection}>
                    Warp revolutionizes international transfers with intelligent multi-chain routing. 
                    Our advanced backend scans 15+ exchanges across multiple networks to find the 
                    fastest, most cost-effective path for your money—delivering superior rates 
                    and same-day settlement.
                  </BrandDescription>
                </ScrollAnimations>
                
                <ScrollAnimations animationType="fadeIn" delay={0.4}>
                  <ContactLinks isInHorizontalSection={isInHorizontalSection}>
                    <li><Link to="mailto:hello@warp.com" target="_blank">
                      hello@warp.com
                      <span className="underline"></span></Link></li>
                    <li><Link to="https://www.twitter.com/warp" target="_blank">
                      Twitter
                      <span className="underline"></span></Link></li>
                    <li><Link to="https://www.linkedin.com/company/warp" target="_blank">
                      Linkedin
                      <span className="underline"></span></Link></li>
                    <li><Link to="https://www.github.com/warp" target="_blank">
                      GitHub
                      <span className="underline"></span></Link></li>
                  </ContactLinks>
                </ScrollAnimations>
                
                <ScrollAnimations animationType="fadeIn" delay={0.6}>
                  <TimeInfo isInHorizontalSection={isInHorizontalSection}>
                    <TimeColumn>
                      <div>Global Operations</div>
                      <div>24/7 Support</div>
                    </TimeColumn>
                    <TimeColumn>
                      <div>Same-Day Settlement</div>
                      <div>Real-Time Rates</div>
                    </TimeColumn>
                  </TimeInfo>
                </ScrollAnimations>
              </LeftContent>
              
              <RightContent>
                <ScrollAnimations animationType="rotateIn" delay={0.3}>
                  <CSSRotatingCircle 
                    scrollProgress={horizontalProgress} 
                    isInHorizontalSection={isInHorizontalSection} 
                  />
                </ScrollAnimations>
              </RightContent>
            </SectionContent>
          </Section>

          {/* Section 2: Technology, Functionality & Aesthetics */}
          <Section style={{ overflow: 'visible' }}>
            <SectionContent>
              <LeftContent>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '3rem', 
                  width: '100%',
                  maxWidth: '600px',
                  position: 'relative', // Always relative to prevent clipping
                  left: 'auto',
                  top: 'auto',
                  transform: `translateY(${10 - horizontalProgress * 10}px)`,
                  transition: 'all 0.2s ease-out',
                  opacity: 1,
                  zIndex: 10 // Ensure it's above other elements
                }}>
                  <SectionSubtitle 
                    isInHorizontalSection={isInHorizontalSection}
                    style={{
                      opacity: 1, // Keep text visible
                      transform: `translateX(${-10 + horizontalProgress * 10}px)`,
                      transition: 'all 0.3s ease-out'
                    }}
                  >
                    Multi-Chain Liquidity
                    <br />
                    Aggregation Engine.
                  </SectionSubtitle>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '2rem',
                    alignItems: 'start'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '1.5rem',
                      opacity: 1, // Keep text visible
                      transform: `translateX(${-5 + horizontalProgress * 5}px)`,
                      transition: 'all 0.4s ease-out'
                    }}>
                      <SectionDescription isInHorizontalSection={isInHorizontalSection}>
                        Our intelligent routing system scans liquidity pools across Algorand, Polygon, zkSync, and other networks to find the optimal settlement path for every transaction.
                      </SectionDescription>
                      
                      <SectionDescription isInHorizontalSection={isInHorizontalSection}>
                        By aggregating rates from both centralized exchanges (CEX) and decentralized exchanges (DEX), we ensure you always get the best possible exchange rate.
                      </SectionDescription>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '1.5rem',
                      opacity: 1, // Keep text visible
                      transform: `translateX(${-3 + horizontalProgress * 3}px)`,
                      transition: 'all 0.5s ease-out'
                    }}>
                      <SectionDescription isInHorizontalSection={isInHorizontalSection}>
                        Transaction batching and optimization algorithms reduce network fees, with all savings passed directly to you for maximum value.
                      </SectionDescription>
                      
                      <SectionDescription isInHorizontalSection={isInHorizontalSection}>
                        Our transparent rate comparison shows you exactly how much you save compared to traditional banks and competitors like Wise or Remitly.
                      </SectionDescription>
                    </div>
                  </div>
                </div>
              </LeftContent>
              
              <RightContent>
                <div 
                  ref={svgRef}
                  style={{ 
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10
                  }}
                >
                  <WhiteSquareWithCircle />
                </div>
              </RightContent>
            </SectionContent>
          </Section>

          {/* Section 3: Multidisciplinary Integrated Design Approach */}
          <Section ref={thirdSectionRef}>
            <SectionContent>
              <LeftContent>
                <SectionTitle 
                  isInHorizontalSection={isInHorizontalSection}
                  opacity={thirdSectionTextOpacity}
                  transform={`translateX(${Math.min(horizontalProgress * 600, 400)}px) scale(${thirdSectionTextScale})`}
                  style={{ 
                    display: 'block',
                    transition: 'transform 0.1s ease-out'
                  }}
                >
                  Superior Exchange
                  <br />
                  Rate Guarantee
                  <br />
                  Technology.
                </SectionTitle>
              </LeftContent>
              
              <RightContent>
              </RightContent>
            </SectionContent>
          </Section>

          {/* Section 4: Large Header with Rotating Circle */}
          <Section style={{ position: 'relative', overflow: 'visible', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RotatingBackgroundCircle isVerticalSection={isVerticalSection} />
            <SectionContent style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <LargeHeaderText 
                isVerticalSection={isVerticalSection}
                opacity={lastSectionTextOpacity}
                transform={`scale(${lastSectionTextScale})`}
              >
                Change the way
                <br />
                you exchange
              </LargeHeaderText>
            </SectionContent>
          </Section>
        </HorizontalScrollWrapper>
      </HorizontalContainer>
      
      {/* Vertical parallax section with converter */}
      <VerticalSection>
        <ConverterSection>
          <ConverterTitle>Start Converting Now</ConverterTitle>
          <ConverterDescription>
            Experience seamless cross-currency transactions with our intelligent routing system.
            Get the best rates across multiple exchanges and chains.
          </ConverterDescription>
          
          <ConverterWrapper>
            <QuickConverter />
          </ConverterWrapper>
          
          <div style={{ marginTop: '2rem' }}>
            <Link 
              to="/login" 
              style={{
                display: 'inline-block',
                padding: '1rem 2rem',
                backgroundColor: '#000000',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '0.25rem',
                fontWeight: '600',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#000000'}
            >
              Get Started
            </Link>
          </div>
        </ConverterSection>
      </VerticalSection>
    </PageContainer>
  );
};

export default TwooLandingPage;